import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ZoomableImage from '@/components/ZoomableImage/ZoomableImage';

/**
 * Mirrors checklist link defaults: `/…` stays in-app; bare hosts → https://.
 * Opens http(s)/mailto links in a new tab; same-origin paths navigate normally.
 */
function resolveMarkdownHref(raw: string): { href: string; openInNewTab: boolean } {
  const trimmed = raw.trim();
  const isHttp = /^https?:\/\//i.test(trimmed);
  const isPath = trimmed.startsWith('/');
  const isMailto = trimmed.startsWith('mailto:');
  const href =
    isHttp ? trimmed : isPath ? trimmed : isMailto ? trimmed : trimmed ? `https://${trimmed}` : '#';
  const openInNewTab = isHttp || isMailto;
  return { href, openInNewTab };
}

/**
 * Detects markdown links that wrap a single ZoomableImage (lightbox thumbnail only).
 * In that case we flatten to `<a><img /></a>` so we do not nest interactive elements.
 */
function getFlattenLinkedZoomable(children: React.ReactNode): React.ReactElement | undefined {
  const arr = React.Children.toArray(children);
  if (arr.length !== 1) return undefined;
  const first = arr[0];
  if (!React.isValidElement(first)) return undefined;
  if (first.type !== ZoomableImage) return undefined;
  return first;
}

const markdownBehaviorComponents: Partial<Components> = {
  a: ({ href, children }) => {
    const { href: resolved, openInNewTab } = resolveMarkdownHref(String(href ?? ''));
    const flat = getFlattenLinkedZoomable(children);

    if (flat) {
      const { src, alt } = flat.props as { src?: string; alt?: string };
      const srcSafe = typeof src === 'string' ? src : '';
      return (
        <a href={resolved} target="_blank" rel="noopener noreferrer" className="d-inline-block longform-markdown-image-link-wrap">
          <img src={srcSafe} alt={typeof alt === 'string' ? alt : ''} className="longform-markdown-flat-img" loading="lazy" />
        </a>
      );
    }

    if (openInNewTab) {
      return (
        <a href={resolved} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }

    return <a href={resolved}>{children}</a>;
  },
  img: ({ src, alt }) => (
    <ZoomableImage src={typeof src === 'string' ? src : ''} alt={typeof alt === 'string' ? alt : ''} />
  ),
  /** Horizontal scroll without styling table cells in TS (handled by `.longform` CSS). */
  table: ({ children, ...props }) => (
    <div className="longform-table-scroll">
      <table {...props}>{children}</table>
    </div>
  ),
};

export type MarkdownTextProps = { content: string | null | undefined };

/**
 * Core markdown renderer — `remark-gfm` for tables, strike-through, tasks, autolinks, etc.
 * Typography and spacing live under global `.longform` (see `_longform.scss`); wrapping blocks
 * in `<div className="longform">` applies the design system prose.
 *
 * Behavioral overrides here: links (new tab rules + flattened image links), Zoomable images, scroll for tables.
 */
export function MarkdownText({ content }: MarkdownTextProps): React.ReactElement | null {
  const text = content ?? '';
  if (!String(text).trim()) {
    return null;
  }

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownBehaviorComponents}>
      {text}
    </ReactMarkdown>
  );
}

export default MarkdownText;
